import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import {
  NfceDownloadType,
  NfceEventStatus,
  NfceInutilizacaoRequest,
  NfceIssueResponse,
  NfcePayload,
  NotaFiscalConsumidor
} from "@interfaces/nfce";
import { HttpService } from "./http.service";

export interface NfceListResponse {
  data: NotaFiscalConsumidor[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Cliente das rotas `v1/nfce` do nf-server (Cupom Fiscal, modelo 65). Mesma
 * forma do `NfeService`; o que muda é o que a NFC-e não tem (carta de
 * correção, reimpressão) e o que só ela tem (XML de cancelamento, e-mail,
 * inutilização).
 */
@Injectable({
  providedIn: "root"
})
export class NfceService {
  url = `${environment.api.mv.nf}/nfce`;

  constructor(private readonly httpService: HttpService) {}

  issue(payload: NfcePayload) {
    return this.httpService.post<NfceIssueResponse>(this.url, payload);
  }

  fetchNfces(
    dataInicial: string | null,
    dataFinal: string | null,
    page = 1,
    limit = 10
  ) {
    const params: string[] = [];
    if (dataInicial) params.push(`dataInicial=${dataInicial}`);
    if (dataFinal) params.push(`dataFinal=${dataFinal}`);
    params.push(`page=${page}`, `limit=${limit}`);

    return this.httpService.get<NfceListResponse>(
      `${this.url}?${params.join("&")}`
    );
  }

  syncNfces(dataInicial: string | null, dataFinal: string | null) {
    const params: string[] = [];
    if (dataInicial) params.push(`dataInicial=${dataInicial}`);
    if (dataFinal) params.push(`dataFinal=${dataFinal}`);

    const queryString = params.length ? `?${params.join("&")}` : "";

    return this.httpService.get(`${this.url}/atualizar${queryString}`);
  }

  fetchResumo(idNota: string) {
    return this.httpService.get(`${this.url}/${idNota}/resumo`);
  }

  fetchResumoByIntegracao(idIntegracao: string) {
    return this.httpService.get(
      `${this.url}/integracao/${idIntegracao}/resumo`
    );
  }

  downloadNfceFile(idNota: string, type: NfceDownloadType) {
    return this.httpService.getBlob(`${this.url}/download/${type}/${idNota}`);
  }

  getExternalNfceDownloadLink(idNota: string, type: NfceDownloadType) {
    return this.httpService.get<{ url: string }>(
      `${this.url}/download/${type}/${idNota}/external-link`
    );
  }

  requestNfceCancellation(idNota: string, justificativa?: string) {
    return this.httpService.post(`${this.url}/${idNota}/cancelamento`, {
      ...(justificativa?.trim() && { justificativa: justificativa.trim() })
    });
  }

  fetchCancellationStatus(idNota: string) {
    return this.httpService.get<NfceEventStatus>(
      `${this.url}/${idNota}/cancelamento/status`
    );
  }

  sendEmail(idNota: string, destinatarios?: string[]) {
    const body = destinatarios?.length ? { destinatarios } : { reenvio: true };

    return this.httpService.post(`${this.url}/${idNota}/email`, body);
  }

  inutilizar(request: NfceInutilizacaoRequest) {
    return this.httpService.post<{ message?: string; protocol?: string }>(
      `${this.url}/inutilizacao`,
      request
    );
  }

  fetchInutilizacaoStatus(protocol: string) {
    return this.httpService.get<NfceEventStatus>(
      `${this.url}/inutilizacao/${protocol}/status`
    );
  }
}
